import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { addMonths, endOfMonth, sameDay, dateOnly, categorize, computeTodayAvailability } from "../lib/calendarUtils";
import TodayAvailabilityPanel from "../components/TodayAvailabilityPanel";
import DayBookingsDialog from "../components/DayBookingsDialog";
import { useAuth } from "../contexts/AuthContext";
import { permVisible } from "../lib/permissions";
import { PillTabs, PageSubtitle } from "../components/lux";

const CATS = ["Coupes", "Sedans", "SUVs", "END"];

export default function AvailabilityPage() {
  const [vehicles, setVehicles] = useState([]);
  const [booked, setBooked] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [hlVehicle, setHlVehicle] = useState(null);
  const [hlDate, setHlDate] = useState(null);
  const [cat, setCat] = useState("Coupes");
  const [picker, setPicker] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  // Clicking a booking bar opens the full booking detail — but only for
  // accounts explicitly granted `fleet_booking_details` (or admin).
  const canOpenBooking = permVisible(user, "fleet_booking_details") === true || user?.role === "admin";

  useEffect(() => {
    let active = true;
    const load = async (silent) => {
      try {
        const { data } = await api.get("/availability");
        if (!active) return;
        setVehicles(data.vehicles || []);
        setBooked(data.booked || []);
      } catch (e) { if (!silent) toast.error(formatApiError(e)); }
    };
    load(false);
    // Auto-refresh so new bookings (incl. from other users) appear without a manual reload.
    const interval = setInterval(() => load(true), 20000);
    const onFocus = () => load(true);
    const onVisible = () => { if (document.visibilityState === "visible") load(true); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const monthLabel = cursor.toLocaleString(undefined, { month: "long", year: "numeric" });
  const fmtDT = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
  const fmtOut = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

  const monthDays = useMemo(() => {
    const last = endOfMonth(cursor).getDate();
    const arr = [];
    for (let i = 1; i <= last; i++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
    return arr;
  }, [cursor]);

  const grouped = useMemo(() => {
    const g = { Coupes: [], Sedans: [], SUVs: [], END: [] };
    [...vehicles]
      .filter((v) => v.status !== "retired")
      .sort((a, b) => (a.make || "").localeCompare(b.make || "") || (a.model || "").localeCompare(b.model || ""))
      .forEach((v) => g[categorize(v)].push(v));
    return g;
  }, [vehicles]);

  const vehicleBookings = useMemo(() => {
    const m = {};
    for (const b of booked) {
      if (!b.vehicle_id) continue;
      const s = dateOnly(b.start_datetime);
      const e = dateOnly(b.end_datetime) || s;
      if (!s) continue;
      (m[b.vehicle_id] ||= []).push({
        id: b.id, s, e, type: b.booking_type, status: b.status, out: b.start_datetime, ret: b.end_datetime,
        customer: b.customer_name || "", from: b.start_location || "", to: b.end_location || "", agent: b.salesperson || "",
      });
    }
    for (const k of Object.keys(m)) m[k].sort((a, b2) => String(a.out || "").localeCompare(String(b2.out || "")));
    return m;
  }, [booked]);

  const cats = useMemo(() => CATS.filter((c) => (grouped[c] || []).length > 0), [grouped]);
  const rows = grouped[cat] || [];
  const todayView = useMemo(() => computeTodayAvailability(vehicles.filter((v) => v.status !== "retired"), booked), [vehicles, booked]);

  useEffect(() => {
    if (cats.length && !cats.includes(cat)) setCat(cats[0]);
  }, [cats, cat]);

  return (
    <div className="space-y-6" data-testid="availability-page">
      <PageSubtitle>
        {vehicles.filter((v) => v.status !== "retired").length} vehicles · timeline of confirmed &amp; pending holds for {monthLabel}
      </PageSubtitle>

      <TodayAvailabilityPanel
        today={todayView}
        sanitized={!canOpenBooking}
        onOpen={(id) => navigate(`/bookings/view/${id}`)}
      />

      <div className="flex items-center justify-between flex-wrap gap-4">
        <PillTabs
          tabs={cats.map((c) => ({ key: c, label: c, count: grouped[c].length, testid: `avail-tab-${c.toLowerCase()}` }))}
          value={cat}
          onChange={setCat}
          testid="avail-tabs"
        />
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-full h-9 w-9" onClick={() => setCursor(addMonths(cursor, -1))} data-testid="avail-prev-month"><CaretLeft size={14} /></Button>
          <div className="font-heading text-lg min-w-[10rem] text-center" data-testid="avail-month">{monthLabel}</div>
          <Button variant="outline" size="icon" className="rounded-full h-9 w-9" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="avail-next-month"><CaretRight size={14} /></Button>
          <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setCursor(new Date())}>Today</Button>
        </div>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <div className="grid min-w-max" style={{ gridTemplateColumns: `200px repeat(${monthDays.length}, minmax(26px,1fr))` }}>
          <div className="px-4 py-3 label-mono border-b border-r border-border bg-card sticky left-0 z-20">{cat}</div>
          {monthDays.map((d) => {
            const dk = d.toDateString();
            const colActive = hlDate === dk;
            const isToday = sameDay(d, new Date());
            return (
              <div key={dk} onClick={() => setHlDate(colActive ? null : dk)} data-testid={`date-header-${d.getDate()}`}
                className={`text-center py-3 text-xs tnum border-b border-r border-border/60 cursor-pointer transition-colors hover:bg-white/[0.04] ${isToday ? "text-primary font-medium" : "text-muted-foreground"} ${colActive ? "ring-1 ring-inset ring-primary bg-primary/10 text-primary" : ""} ${hlDate && !colActive ? "opacity-40" : ""}`}>{d.getDate()}</div>
            );
          })}
          {rows.length === 0 && (
            <div className="col-span-full px-3 py-12 text-center text-muted-foreground text-sm">No vehicles in this category.</div>
          )}
          {rows.flatMap((v) => [
            <div key={`l-${v.id}`} onClick={() => setHlVehicle(hlVehicle === v.id ? null : v.id)} data-testid={`vehicle-label-${v.id}`}
              className={`px-4 py-2 text-xs border-b border-r border-border/50 sticky left-0 z-10 bg-card cursor-pointer transition-colors hover:bg-white/[0.04] ${hlVehicle === v.id ? "ring-1 ring-inset ring-primary bg-primary/10" : ""} ${hlVehicle && hlVehicle !== v.id ? "opacity-40" : ""}`}>
              <div className="font-heading text-sm truncate">{v.make} {v.model}{v.status === "maintenance" && <span className="ml-1.5 text-amber-400 text-[9px] uppercase tracking-wider">● Maint</span>}</div>
              <div className="text-[10px] text-muted-foreground font-mono">{v.license_plate}</div>
            </div>,
            ...monthDays.map((d) => {
              const dk = d.toDateString();
              const rowActive = hlVehicle === v.id;
              const colActive = hlDate === dk;
              const anyHl = hlVehicle || hlDate;
              const cellHl = (rowActive || colActive) ? "ring-1 ring-inset ring-primary z-[5] relative" : (anyHl ? "opacity-40" : "");
              if (v.status === "maintenance") {
                return (
                  <div key={`${v.id}-${d.getDate()}`} className={`flex items-center justify-center border-b border-r border-border/40 bg-amber-500/45 ${cellHl}`} style={{ minHeight: 30 }} title="In maintenance — unavailable" data-testid={`maint-cell-${v.id}-${d.getDate()}`}>
                    <span className="text-amber-950 font-bold text-[11px] leading-none">✕</span>
                  </div>
                );
              }
              const segs = vehicleBookings[v.id] || [];
              const hits = segs.filter((s) => d >= s.s && d <= s.e);
              let cls = "bg-emerald-500/[0.08]", title = "Available";
              const typeLabel = (t) => (t === "chauffeur" ? "Chauffeur" : t === "tour" ? "Tour" : "Rental");
              if (hits.length) {
                const confirmedHit = hits.find((h) => h.status !== "submitted");
                const confirmed = !!confirmedHit;
                const sel = confirmedHit || hits[0];
                const selChauffeurTour = sel.type === "chauffeur" || sel.type === "tour";
                cls = confirmed ? (selChauffeurTour ? "bg-sky-500/55" : "bg-red-500/50") : "bg-amber-400/50";
                if (hits.length > 1) {
                  title = `${hits.length} overlapping bookings:\n` + hits
                    .map((h) => `• ${h.status !== "submitted" ? "Confirmed" : "Submitted"} · ${typeLabel(h.type)} · out ${fmtOut(h.out)}${h.ret ? ` · returns ${fmtDT(h.ret)}` : ""}`)
                    .join("\n");
                } else {
                  const h = hits[0];
                  title = `${confirmed ? "Confirmed booking" : "Submitted (pending)"} · ${typeLabel(h.type)}\nOut ${fmtOut(h.out)}${h.ret ? ` · Returns ${fmtOut(h.ret)}` : ""}`;
                }
              }
              const stars = hits.length >= 2 ? "★".repeat(hits.length) : "";
              const clickable = canOpenBooking && hits.length > 0;
              const openBooking = () => {
                if (!clickable) return;
                // Double-booked day: let the user flip through every booking on it.
                if (hits.length > 1) {
                  setPicker({ vehicle: v, date: d, hits });
                  return;
                }
                if (hits[0]?.id) navigate(`/bookings/view/${hits[0].id}`);
              };
              return (
                <div
                  key={`${v.id}-${d.getDate()}`}
                  onClick={openBooking}
                  className={`flex items-center justify-center border-b border-r border-border/40 ${cls} ${cellHl} ${clickable ? "cursor-pointer hover:brightness-125 transition-[filter]" : ""}`}
                  style={{ minHeight: 30 }}
                  title={clickable ? `${title}\n(click to ${hits.length > 1 ? `switch between all ${hits.length} bookings` : "view booking"})` : title}
                  data-testid={`avail-cell-${v.id}-${d.getDate()}`}
                >
                  {stars && <span className="text-[8px] leading-none font-bold text-black tracking-tighter">{stars}</span>}
                </div>
              );
            }),
          ])}
        </div>
      </div>

      <div className="flex gap-2.5 text-xs flex-wrap">
        {[
          { c: "bg-emerald-500/25 border-emerald-500/40", l: "Available" },
          { c: "bg-amber-400/40 border-amber-400/50", l: "Submitted (pending review)" },
          { c: "bg-red-500/40 border-red-500/50", l: "Confirmed rental" },
          { c: "bg-sky-500/40 border-sky-500/50", l: "Confirmed chauffeur / tour" },
          { c: "bg-white/5 border-white/10", l: "★ per overlapping booking (double-book)" },
        ].map((it) => (
          <span key={it.l} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-white/[0.02] text-muted-foreground">
            <span className={`w-2.5 h-2.5 rounded-sm border ${it.c}`} /> {it.l}
          </span>
        ))}
      </div>
      <DayBookingsDialog picker={picker} onClose={() => setPicker(null)} onOpenBooking={(id) => navigate(`/bookings/view/${id}`)} />
    </div>
  );
}
